import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, makeStyles } from '@material-ui/core/styles'

export const appHeaderHeight = 60
// Harmony dropdown-menu shadow (user menu + help menu popovers).
const HARMONY_MENU_SHADOW = '0 8px 24px rgba(42, 52, 71, 0.16)'

export interface StyleProps {
    isStudioIconLoaded: boolean
}

export const useAppHeaderStyles = makeStyles((theme: Theme) => {
    // Shared 40px pill shape + hover tint for the lightbulb button and the
    // Home/Map/Studio nav icons in the Harmony light header.
    const iconButtonBase = {
        width: pxToRem(40),
        height: pxToRem(40),
        padding: pxToRem(9),
        borderRadius: '999px',
        color: theme.harmony.colors.textHeader,
        transition: 'background 150ms ease',
    }
    const iconButtonHoverBase = {
        backgroundColor: theme.harmony.colors.hoverBg,
        color: theme.harmony.colors.brandMain,
    }

    return {
        fullHeight: {
            height: appHeaderHeight,
        },
        appHeaderContainer: {
            zIndex: theme.zIndex.appBar + 1,
            position: 'relative',
        },
        appBarContainer: {
            zIndex: theme.zIndex.appBar + 1,
            background: `linear-gradient(90.06deg, #2F4858 49.48%, #5F788A 100%)`,
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            flexDirection: 'row',
        },
        actionBar: {
            zIndex: theme.zIndex.appBar - 1,
            background: theme.palette.grey[700],
            transition: '100ms',
        },
        whiteFont: {
            color: theme.palette.common.white,
        },
        iconButton: {
            borderRadius: 0,
            opacity: 0.45,
            width: appHeaderHeight,
            transition: '200ms',

            '&:hover': {
                opacity: 1,
            },
        },
        reset: {
            padding: 0,
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        iconButtonActive: {
            backgroundColor: `rgba(0,0,0,0.25)`,
            opacity: 1,

            '&:hover': {
                backgroundColor: `rgba(0,0,0,0.25)`,
            },
        },
        hasDivider: {
            '&:after,&:before': {
                content: '""',
                width: 1,
                height: appHeaderHeight,
                backgroundColor: 'rgba(255,255,255,0.25)',

                '&:hover': {
                    backgroundColor: theme.palette.common.white,
                },
            },
        },
        appHeaderLeftContentContainer: {
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '100%',
            maxWidth: pxToRem(580),
            [theme.breakpoints.down('xs')]: {
                width: 'unset',
            },
        },
        fixedWidthContent: {
            width: 330,
        },
        appHeaderContentContainer: {
            height: '100%',
            paddingLeft: theme.spacing(2.6),
            flexWrap: 'nowrap',
        },
        fullPageMenuContainer: {
            zIndex: theme.zIndex.appBar,
            top: appHeaderHeight,
        },
        backdrop: {
            zIndex: theme.zIndex.appBar,
            backgroundColor: 'rgba(0,0,0,0.85)',
        },
        smallScreenWarningText: {
            color: theme.palette.common.white,
            textAlign: 'center',
        },
        warningCloseButton: {
            position: 'absolute',
            top: appHeaderHeight,
            right: 0,
        },
        deltekIcon: {
            '&.MuiSvgIcon-root': {
                width: pxToRem(230),
                fontSize: pxToRem(80),
            },
        },
        logoContainer: {
            paddingRight: theme.spacing(7),
            paddingLeft: theme.spacing(1),
            '@media (max-width: 1024px)': {
                paddingRight: theme.spacing(3),
            },
        },
        studioLoading: {
            padding: theme.spacing(2),
            '& .MuiCircularProgress-root': {
                color: '#b1abab',
            },
        },
        studioIcon: {
            display: (props: StyleProps) =>
                props.isStudioIconLoaded ? 'block' : 'none',
        },
        flex: {
            display: 'flex',
        },
        lightBulbMain: {
            height: appHeaderHeight,
            maxWidth: 50,
            opacity: 1,
            padding: 0,
        },
        lightBulbBtn: {
            height: '100%',
            borderRadius: 0,
            '&.Mui-focusVisible': {
                backgroundColor: theme.palette.action.focus,
            },
        },
        lightBulbIcon: {
            width: 25,
            height: 25,
            color: theme.palette.common.white,
            fill: 'none',
            stroke: 'currentColor',
            marginTop: -2,
        },
        lightBulbIconLight: {
            color: theme.harmony.colors.textHeader,
            width: pxToRem(22),
            height: pxToRem(22),
            '& path': {
                strokeWidth: 1.6,
            },
        },
        lightBulbMainLight: {
            height: 'auto',
            maxWidth: pxToRem(40),
        },
        lightBulbBtnLight: {
            ...iconButtonBase,
            '&:hover': {
                ...iconButtonHoverBase,
                '& $lightBulbIconLight': {
                    color: theme.harmony.colors.brandMain,
                },
            },
        },
        harmonyFocusRing: {
            '&:focus-visible': {
                outline: `2px solid ${theme.harmony.colors.focusRing}`,
                outlineOffset: pxToRem(2),
            },
        },
        appBarContainerLight: {
            background: theme.harmony.colors.headerBackground,
            // AppBar's primary color cascades white via currentColor; force dark so vendor icons stay visible.
            color: theme.harmony.colors.textHeader,
            boxShadow: `${theme.harmony.colors.panelShadow}, 0px 1px 0px ${theme.harmony.colors.shadow}`,
        },
        accentStripLight: {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: pxToRem(-3),
            height: pxToRem(6),
            borderRadius: pxToRem(6),
            background: theme.harmony.colors.headerAccentGradient,
        },
        logoContainerLight: {
            paddingTop: pxToRem(4),
            paddingRight: pxToRem(12),
            paddingBottom: pxToRem(4),
            paddingLeft: pxToRem(4),
            marginRight: pxToRem(8),
        },
        logoImgLight: {
            height: pxToRem(30),
            display: 'block',
        },
        leftClusterLight: {
            display: 'flex',
            alignItems: 'center',
            gap: pxToRem(8),
        },
        navIconButtonLight: {
            ...iconButtonBase,
            '&:hover': iconButtonHoverBase,
        },
        navIconButtonLightActive: {
            backgroundColor: theme.harmony.colors.activeBg,
            color: theme.harmony.colors.brandMain,
            '&:hover': {
                backgroundColor: theme.harmony.colors.activeBg,
                color: theme.harmony.colors.brandMain,
            },
        },
        navIconLight: {
            width: pxToRem(22),
            height: pxToRem(22),
        },
        responsiveMenusLightOverride: {
            alignItems: 'center',
            // Sizing/position mirrors navIconButtonLight so bell/help match the other light nav icons.
            '& [data-testid="globalNotificationButton"], & [data-testid="globalHelpButton"]': {
                color: `${theme.harmony.colors.textHeader} !important`,
                width: '40px !important',
                height: '40px !important',
                minHeight: '40px !important',
                padding: '9px !important',
                borderRadius: '999px !important',
                marginTop: '10px !important',
                transition: 'background 150ms ease',
            },
            '& [data-testid="globalNotificationButton"] svg, & [data-testid="globalHelpButton"] svg': {
                width: '22px !important',
                height: '22px !important',
            },
            '& [data-testid="globalNotificationButton"]:hover, & [data-testid="globalHelpButton"]:hover': {
                backgroundColor: `${theme.harmony.colors.hoverBg} !important`,
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            '& [data-testid="globalNotificationButton"]:focus-visible, & [data-testid="globalHelpButton"]:focus-visible, & [data-testid="globalUserDropdownButton"]:focus-visible': {
                outline: `2px solid ${theme.harmony.colors.focusRing} !important`,
                outlineOffset: '2px !important',
            },
            // HelpMenuDropdown's own open-state overlay assumes the dark AppBar; use the same pale-blue active state as the other nav icons instead.
            '& [data-testid="globalHelpButton"][class*="borderlessHighlighted"]': {
                backgroundColor: `${theme.harmony.colors.activeBg} !important`,
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            '& [data-testid="AvatarIconUserMenuDropdown"]': {
                display: 'flex !important',
                alignItems: 'center !important',
            },
            '& [data-testid="globalUserDropdownButton"]': {
                color: `${theme.harmony.colors.textHeader} !important`,
                backgroundColor: 'transparent !important',
                // The wrapper below owns the pill's height (34px, matching ContextPill).
                height: '100% !important',
                minHeight: '0 !important',
                // Cancels UserMenuDropdown's own hardcoded 19px paddingRight on this button.
                paddingRight: '0 !important',
            },
            // Drop UserMenuDropdown's 8vw name truncation (ContextPill never truncates) + apply Harmony user-name font.
            '& [class*="nameText"]': {
                maxWidth: 'none !important',
                whiteSpace: 'nowrap !important',
                fontFamily: `${theme.harmony.typography.userName.fontFamily} !important`,
                fontSize: `${theme.harmony.typography.userName.fontSize} !important`,
                fontWeight: `${theme.harmony.typography.userName.fontWeight} !important`,
            },
            '& [class*="limitedWidth"], & [class*="extendedWidth"]': {
                maxWidth: 'none !important',
            },
            // Shrinks UserAvatar's default 32px image and widens its right margin to match the mockup's tighter pill spacing.
            '& [data-testid="profileImage"]': {
                width: '28px !important',
                height: '28px !important',
                margin: '1px 6px 1px 1px !important',
            },
            // Avatar-initials fallback (no photo): Harmony "12 Regular Lexend" on a solid brandMain swatch.
            '& [data-testid="profileAvatarIcon"]': {
                fontFamily: `${theme.harmony.typography.avatarInitials.fontFamily} !important`,
                fontSize: `${theme.harmony.typography.avatarInitials.fontSize} !important`,
                fontWeight: `${theme.harmony.typography.avatarInitials.fontWeight} !important`,
                color: '#FFFFFF !important',
                backgroundColor: `${theme.harmony.colors.brandMain} !important`,
            },
            // No pill chrome — avatar/name/chevron sit flush on the header; activeDropdown lives on the parent <div>, not this button.
            '& [class*="userMenuButton"]': {
                backgroundColor: 'transparent !important',
                border: 'none !important',
                height: '34px !important',
                padding: '0 4px !important',
                display: 'inline-flex !important',
                alignItems: 'center !important',
            },
            '& [class*="userMenuButton"]:hover [data-testid="globalUserDropdownButton"]': {
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            // Tints the same brand blue as :hover while the menu is open; ordered after :hover so it still wins on click.
            '& [class*="userMenuButton"][class*="activeDropdown"] [data-testid="globalUserDropdownButton"]': {
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            // UserMenuDropdown's end-icon never flips on open, so flip it with a CSS transform instead.
            '& [class*="userMenuButton"][class*="activeDropdown"] .MuiButton-endIcon svg': {
                transform: 'rotate(180deg)',
            },
        },
        // Both dropdown menus portal to document.body via MUI's <Menu>, so a <body> class (toggled below) is the only way to reach them.
        '@global': {
            'body.harmonyHeaderActive [data-testid="globalPopoverUserMenu"] .MuiMenu-paper': {
                borderRadius: '8px !important',
                boxShadow: `${HARMONY_MENU_SHADOW} !important`,
            },
            'body.harmonyHeaderActive [data-testid^="userDropdownItem"] .MuiListItemText-primary': {
                color: `${theme.harmony.colors.textHeader} !important`,
            },
            'body.harmonyHeaderActive [data-testid^="userDropdownItem"]:hover': {
                backgroundColor: `${theme.harmony.colors.hoverBg} !important`,
            },
            'body.harmonyHeaderActive [data-testid^="userDropdownItem"]:hover .MuiListItemText-primary': {
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            // Help menu items don't share a common data-testid prefix, so scope by the container testid + vendor's popoverMenuItem class instead.
            'body.harmonyHeaderActive [data-testid="globalPopoverHelpMenuContainer"] .MuiMenu-paper': {
                borderRadius: '8px !important',
                boxShadow: `${HARMONY_MENU_SHADOW} !important`,
            },
            'body.harmonyHeaderActive [data-testid="globalPopoverHelpMenuContainer"] .popoverMenuItem .MuiListItemText-primary': {
                color: `${theme.harmony.colors.textHeader} !important`,
            },
            'body.harmonyHeaderActive [data-testid="globalPopoverHelpMenuContainer"] .popoverMenuItem:hover': {
                backgroundColor: `${theme.harmony.colors.hoverBg} !important`,
            },
            'body.harmonyHeaderActive [data-testid="globalPopoverHelpMenuContainer"] .popoverMenuItem:hover .MuiListItemText-primary': {
                color: `${theme.harmony.colors.brandMain} !important`,
            },
        },
    }
})
